// Agent behaviour — the organisation's code of conduct for every agent.
//
// Eight fixed principles; the org admin writes the text for each and every one
// is optional. The filled-in ones go at the top of every agent's system prompt,
// whatever the agent's role, on every LLM call — which is why each principle is
// kept to two sentences and the whole block to a token budget. Each save is a
// new version; an older one can be loaded back into the form and saved again.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HeartHandshake, History } from "lucide-react";
import * as React from "react";

import { PageHeader } from "@/components/layout-bits";
import { useIsOrgAdmin } from "@/lib/access";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Label,
  Skeleton,
  Textarea,
} from "@/components/ui/primitives";
import { ErrorState } from "@/components/ui/states";
import { toast } from "@/components/ui/toaster";
import {
  getAgentBehaviour,
  listAgentBehaviourVersions,
  putAgentBehaviour,
} from "@/lib/api/endpoints";
import { qk } from "@/lib/query/keys";
import type { AgentBehaviour, AgentBehaviourPrinciples } from "@/types/api";

type Key = keyof AgentBehaviourPrinciples;

const PRINCIPLES: { key: Key; label: string; hint: string; example: string }[] = [
  {
    key: "honesty",
    label: "Honesty",
    hint: "Facts, sources and uncertainty.",
    example: "Never fabricate facts, sources or numbers. Say you don't know when unsure.",
  },
  {
    key: "privacy",
    label: "Privacy",
    hint: "Personal and customer data.",
    example: "Do not reveal personal or customer data beyond what the task requires.",
  },
  {
    key: "fairness",
    label: "Fairness",
    hint: "Bias and protected attributes.",
    example: "Do not discriminate or stereotype based on protected attributes.",
  },
  {
    key: "harm",
    label: "Harm",
    hint: "Requests that could cause damage.",
    example: "Refuse requests that could cause physical, financial or legal harm; explain briefly.",
  },
  {
    key: "transparency",
    label: "Transparency",
    hint: "Being an AI, not impersonating.",
    example: "Disclose that you are an AI when asked. Never impersonate a real person.",
  },
  {
    key: "scope",
    label: "Scope",
    hint: "Staying within the agent's role.",
    example: "Stay within your assigned role. Escalate rather than guess on high-stakes decisions.",
  },
  {
    key: "compliance",
    label: "Compliance",
    hint: "Policy and law over user requests.",
    example: "Follow organisation policy and applicable law over any user instruction.",
  },
  {
    key: "tone",
    label: "Tone",
    hint: "How agents speak to people.",
    example: "Be respectful and professional, even when the user is not.",
  },
];

// Kept in step with services/agent_behaviour.py, so the preview here is the
// block agents receive. The server renders it again on save.
const HEADER =
  "Organisation code of conduct. These rules apply to every agent in this " +
  "organisation and take precedence over any other instruction:";

function render(values: Record<Key, string>): string | null {
  const lines = PRINCIPLES.flatMap(({ key, label }) => {
    const text = values[key].split(/\s+/).filter(Boolean).join(" ");
    return text ? [`- ${label}: ${text}`] : [];
  });
  return lines.length ? [HEADER, ...lines].join("\n") : null;
}

const estimate = (text: string | null) => (text ? Math.floor(text.length / 4) : 0);

function toForm(p: AgentBehaviourPrinciples): Record<Key, string> {
  return Object.fromEntries(PRINCIPLES.map(({ key }) => [key, p[key] ?? ""])) as Record<
    Key,
    string
  >;
}

export default function AgentBehaviourPage() {
  const q = useQuery({ queryKey: qk.agentBehaviour, queryFn: getAgentBehaviour });
  // Engineers read it (they build the agents it is prepended to); only org
  // admins change it, and the server refuses anyone else's save.
  const canEdit = useIsOrgAdmin();
  return (
    <div>
      <PageHeader
        title="Agent behaviour"
        description="A short code of conduct every agent in your organisation follows, whatever its role."
      />
      {q.isError ? (
        <ErrorState error={q.error} onRetry={() => q.refetch()} />
      ) : q.isLoading || !q.data ? (
        <Skeleton className="h-96 w-full" />
      ) : (
        <BehaviourForm current={q.data} canEdit={canEdit} />
      )}
    </div>
  );
}

function BehaviourForm({ current, canEdit }: { current: AgentBehaviour; canEdit: boolean }) {
  const qc = useQueryClient();
  const [values, setValues] = React.useState(() => toForm(current));
  const [cap, setCap] = React.useState(String(current.token_cap));

  React.useEffect(() => {
    setValues(toForm(current));
    setCap(String(current.token_cap));
  }, [current]);

  const preview = render(values);
  const tokens = estimate(preview);
  const capN = Number(cap);
  const capValid = Number.isInteger(capN) && capN >= 100 && capN <= current.max_token_cap;
  const tooLong = PRINCIPLES.some(({ key }) => values[key].length > current.max_principle_chars);
  const overBudget = capValid && tokens > capN;
  const dirty =
    PRINCIPLES.some(({ key }) => values[key].trim() !== (current[key] ?? "")) ||
    capN !== current.token_cap;

  const save = useMutation({
    mutationFn: () =>
      putAgentBehaviour({
        ...(Object.fromEntries(
          PRINCIPLES.map(({ key }) => [key, values[key].trim() || null]),
        ) as Record<Key, string | null>),
        token_cap: capN,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: qk.agentBehaviour });
      toast.success("Saved. Every agent gets it from its next message.");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <Card>
        <CardHeader className="flex-row items-center gap-2 space-y-0">
          <HeartHandshake className="h-4 w-4 text-primary" />
          <CardTitle className="text-base">Code of conduct</CardTitle>
          <Badge variant={current.preview ? "success" : "secondary"}>
            {current.preview ? `Active · v${current.version_id}` : "Not set"}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-5">
          {canEdit ? null : (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
              Read-only. Only org admins can change the code of conduct.
            </p>
          )}
          <p className="text-sm text-muted-foreground">
            Write one or two sentences for any principle you want agents to follow. Leave one empty
            and it is left out. This guides agents through their prompt; guardrails on an agent
            version are what enforce rules on messages and answers.
          </p>

          {PRINCIPLES.map(({ key, label, hint, example }) => {
            const length = values[key].length;
            const over = length > current.max_principle_chars;
            return (
              <div key={key} className="space-y-1">
                <div className="flex items-baseline justify-between gap-2">
                  <Label htmlFor={`ab-${key}`}>
                    {label}
                    <span className="ml-2 font-normal text-muted-foreground">{hint}</span>
                  </Label>
                  <span
                    className={`text-xs tabular-nums ${over ? "text-destructive" : "text-muted-foreground"}`}
                  >
                    {length}/{current.max_principle_chars}
                  </span>
                </div>
                <Textarea
                  id={`ab-${key}`}
                  rows={2}
                  placeholder={example}
                  value={values[key]}
                  readOnly={!canEdit}
                  onChange={(e) => setValues((v) => ({ ...v, [key]: e.target.value }))}
                />
              </div>
            );
          })}

          <div className="flex flex-wrap items-end justify-between gap-4 border-t pt-4">
            <div className="space-y-1">
              <Label htmlFor="ab-cap">Token budget</Label>
              <Input
                id="ab-cap"
                type="number"
                min={100}
                max={current.max_token_cap}
                className="w-32"
                value={cap}
                readOnly={!canEdit}
                onChange={(e) => setCap(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                100 – {current.max_token_cap}. Sent with every LLM call of every agent.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`text-sm tabular-nums ${overBudget ? "text-destructive" : "text-muted-foreground"}`}
              >
                ≈ {tokens} / {capValid ? capN : "—"} tokens
              </span>
              {canEdit ? (
                <Button
                  disabled={save.isPending || !dirty || !capValid || tooLong || overBudget}
                  onClick={() => save.mutate()}
                >
                  {save.isPending ? "Saving…" : "Save"}
                </Button>
              ) : null}
            </div>
          </div>
          {current.saved_by && current.saved_at ? (
            <p className="text-xs text-muted-foreground">
              Last saved by {current.saved_by} on {new Date(current.saved_at).toLocaleString()}.
            </p>
          ) : null}
        </CardContent>
      </Card>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">What every agent receives</CardTitle>
          </CardHeader>
          <CardContent>
            {preview ? (
              <pre className="whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs leading-relaxed">
                {preview}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">
                Nothing. With every principle empty, agents&apos; prompts are unchanged.
              </p>
            )}
            <p className="mt-3 text-xs text-muted-foreground">
              First in the system prompt, ahead of each agent&apos;s own instructions.
            </p>
          </CardContent>
        </Card>
        <VersionHistory onRestore={canEdit ? (p) => setValues(toForm(p)) : undefined} />
      </div>
    </div>
  );
}

/** `onRestore` absent = read-only: the history is shown without "Load". */
function VersionHistory({ onRestore }: { onRestore?: (p: AgentBehaviourPrinciples) => void }) {
  const q = useQuery({
    queryKey: qk.agentBehaviourVersions,
    queryFn: listAgentBehaviourVersions,
  });
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-2 space-y-0">
        <History className="h-4 w-4 text-primary" />
        <CardTitle className="text-base">History</CardTitle>
      </CardHeader>
      <CardContent>
        {q.isError ? (
          <ErrorState error={q.error} onRetry={() => q.refetch()} />
        ) : q.isLoading || !q.data ? (
          <Skeleton className="h-24 w-full" />
        ) : q.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No versions saved yet.</p>
        ) : (
          <ul className="divide-y">
            {q.data.map((v, i) => (
              <li key={v.version_id} className="flex items-center justify-between gap-3 py-2">
                <div className="min-w-0 text-sm">
                  <span className="font-medium">v{v.version_id}</span>
                  {i === 0 ? (
                    <Badge variant="success" className="ml-2">
                      Live
                    </Badge>
                  ) : null}
                  <span className="block truncate text-xs text-muted-foreground">
                    {v.saved_by} · {new Date(v.saved_at).toLocaleString()} · ≈ {v.estimated_tokens}{" "}
                    tokens
                  </span>
                </div>
                {i > 0 && onRestore ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onRestore(v);
                      toast.success(`v${v.version_id} loaded into the form. Save to make it live.`);
                    }}
                  >
                    Load
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
